import { call, put, select, takeLatest } from 'redux-saga/effects'
import { IOption } from '../../../common/common.types'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getAgeFromReference, getAgeUnitOptionIdFromReference } from '../../../utilities/dateTime.util'
import { updateValueForPathWithCreate } from '../../../utilities/object.util'
import { initialAuthorizationInformationState } from '../../authorizationInformation/store/authorizationInformation.reducers'
import { initialCmsInformationState } from '../../cmsInformation/store/cmsInformation.reducers'
import { getPcrIdSelector } from '../../home/selectors'
import { initialInsuranceInformationState } from '../../insuranceInformation/store/insuranceInformation.reducers'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { IPatientDemographicsOptionsState } from '../../options/store/options.types'
import { initialPatientAllergiesState } from '../../patientAllergies/store/patientAllergies.reducers'
import { initialPatientDemographicsState } from '../../patientDemographics/store/patientDemographics.reducer'
import { initialPatientImmunizationsState } from '../../patientImmunizations/store/patientImmunizations.reducers'
import { refreshPatientData } from '../../patientInformation/store/patientInformation.actions'
import { initialPatientMedicalHistoryState } from '../../patientMedicalHistory/store/patientMedicalHistory.reducers'
import { initialPatientMedicationsState } from '../../patientMedications/store/patientMedications.reducers'
import { initialPaymentInformationState } from '../../paymentInformation/store/paymentInformation.reducers'
import { initialPcsInformationState } from '../../pcsInformation/store/pcsInformation.reducers'
import { IPcr } from '../../pcr/store/pcr.types'
import { initialRelationshipsAndGuarantorsState } from '../../relationshipsAndGuarantors/store/relationshipsAndGuarantors.reducers'
import { getServiceIdSelector } from '../../user/selectors/getServiceId.selector'
import { getPatientSearchState } from '../selectors/patientSearch.selector'
import { PatientSearchService } from '../services/patientSearch.service'
import { resetSearch, resetSearchState, setIsPatientDataLoading } from '../store/patientSearch.actions'
import { PatientSearchConstants } from '../store/patientSearch.constants'
import {
  IPatientDataRequest,
  IPatientDataResponse,
  IPatientSearchResultDataPoint,
  IPatientSearchState,
} from '../store/patientSearch.types'
import { refreshBillingData } from '../../billing/store/billing.actions'
import { IPatientInformationState } from '../../patientInformation/store/patientInformation.types'
import { getPatientInformationState } from '../../patientInformation/selectors'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import {
  setPatientImmunizationStatus,
  setPatientImmunizationIncompleteReason,
  setImmunizations,
} from '../../patientImmunizations/store/patientImmunizations.actions'
import { noKnownDrugAllergiesNemsis } from '../../patientAllergies/util/getDisabledAllergylist.util'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import i18n from '../../../i18n'
import { initializePatientConflictWithProcessedData } from '../../pulsara/saga/initializePatientConflictSelections.saga'
import { resetLoader } from '../../../components/loader/store/loader.actions'

export function* getPatientRecord() {
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  const serviceConfig: IServiceConfig = yield select(getServiceConfigSelector)
  const pcrDocsService = new PcrDocsService(serviceId)
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const patientSearchState: IPatientSearchState = yield select(getPatientSearchState)
  const patientInformation: IPatientInformationState = yield select(getPatientInformationState)
  const { selectedPtid } = patientSearchState
  const patientDemographicsOptions: IPatientDemographicsOptionsState = yield select(
    getPatientDemographicsOptionsSelector
  )
  const { DEFAULT_WEIGHT_MEASUREMENT, DEFAULT_LENGTH_MEASUREMENT } = serviceConfig
  const { ageUnitOptions, weightUnitOptions, heightUnitOptions, phoneTypeOptions, residenceOptions } =
    patientDemographicsOptions

  if (!selectedPtid) {
    throw Error('ptid does not exist')
  }
  try {
    const patientSearchService = new PatientSearchService(authenticationJwt as string)
    const patientDataRequest: IPatientDataRequest = {
      ptid: selectedPtid,
    }
    const isFromPulsara = window.location.pathname.includes('/pulsara')

    const patientDataResponse: IPatientDataResponse = yield call(
      [patientSearchService, patientSearchService.getPatientData],
      patientDataRequest
    )
    if (patientDataResponse) {
      if (patientDataResponse.data) {
        const defaultWeightUnits = weightUnitOptions.find(
          (option: IOption) => option.name?.toString() === DEFAULT_WEIGHT_MEASUREMENT
        )
        const defaultHeightUnits = heightUnitOptions.find(
          (option: IOption) => option.id?.toString() === DEFAULT_LENGTH_MEASUREMENT
        )
        const { patientData } = patientDataResponse.data
        const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
        const replacedPatientPCR: IPcr = {
          ...pcrDoc,
          ptid: null,
          patient: {
            ...pcrDoc.patient,
            patientDemographics: {
              ...initialPatientDemographicsState,
              weightUnits: defaultWeightUnits || null,
              heightUnits: defaultHeightUnits || null,
            },
            patientAllergies: { ...initialPatientAllergiesState },
            patientMedications: { ...initialPatientMedicationsState },
            patientMedicalHistory: { ...initialPatientMedicalHistoryState },
            patientImmunizations: { ...initialPatientImmunizationsState },
            patientInformation: { ...patientInformation },
          },
          billing: {
            insuranceInformation: { ...initialInsuranceInformationState },
            relationshipsAndGuarantors: { ...initialRelationshipsAndGuarantorsState },
            pcsInformation: { ...initialPcsInformationState },
            authorizationInformation: { ...initialAuthorizationInformationState },
            cmsInformation: { ...initialCmsInformationState },
            paymentInformation: { ...initialPaymentInformationState },
          },
        }

        replacedPatientPCR.ptid = selectedPtid.toString()
        replacedPatientPCR.patient.patientDemographics.servicePtID = patientDataResponse.data.servicePtid
        patientData.forEach((dataPoint: IPatientSearchResultDataPoint) => {
          updateValueForPathWithCreate(replacedPatientPCR, dataPoint.appStateLocation, dataPoint.value)
        })
        if (residenceOptions?.length > 0) {
          const residentValue = replacedPatientPCR?.patient?.patientDemographics?.resident
          if (residentValue != null) {
            const hasResidentName = typeof residentValue === 'object' && !!residentValue?.name
            if (!hasResidentName) {
              const residentId = typeof residentValue === 'object' ? residentValue?.id : residentValue
              const residentNemsis = typeof residentValue === 'object' ? residentValue?.nemsisCode : null
              let matchedResidentOption: IOption | undefined

              if (residentId != null) {
                matchedResidentOption = residenceOptions.find(
                  (opt: IOption) => opt.id === residentId || opt.id === Number(residentId)
                )
              }

              if (!matchedResidentOption && residentNemsis != null) {
                matchedResidentOption = residenceOptions.find(
                  (opt: IOption) => String(opt.nemsisCode) === String(residentNemsis)
                )
              }

              replacedPatientPCR.patient.patientDemographics.resident = matchedResidentOption || null
            }
          }
        }
        if (
          replacedPatientPCR.patient &&
          replacedPatientPCR.patient.patientDemographics &&
          replacedPatientPCR.patient.patientDemographics.dOB
        ) {
          const dob = replacedPatientPCR.patient.patientDemographics.dOB
          const dispatchedDate = replacedPatientPCR.times?.dispatchedDate ?? null
          replacedPatientPCR.patient.patientDemographics.age = getAgeFromReference(dob, dispatchedDate)
          replacedPatientPCR.patient.patientDemographics.ageUnit = ageUnitOptions.find(
            (option: { id: IOption }) => option.id === getAgeUnitOptionIdFromReference(dob, dispatchedDate)
          )
        }
        if (
          defaultWeightUnits &&
          replacedPatientPCR.patient &&
          replacedPatientPCR.patient.patientDemographics &&
          !replacedPatientPCR.patient.patientDemographics.weightUnits
        ) {
          replacedPatientPCR.patient.patientDemographics.weightUnits = defaultWeightUnits
        }
        if (
          defaultHeightUnits &&
          replacedPatientPCR.patient &&
          replacedPatientPCR.patient.patientDemographics &&
          !replacedPatientPCR.patient.patientDemographics.heightUnits
        ) {
          replacedPatientPCR.patient.patientDemographics.heightUnits = defaultHeightUnits
        }
        if (replacedPatientPCR?.patient?.patientDemographics?.employer == null) {
          delete replacedPatientPCR.patient.patientDemographics.employer
        }
        if (replacedPatientPCR?.patient?.patientDemographics?.immunizationStatus) {
          if (!isFromPulsara) {
            yield put(
              setPatientImmunizationStatus(
                replacedPatientPCR?.patient?.patientDemographics?.immunizationStatus,
                replacedPatientPCR?.patient?.patientDemographics?.immunizationStatusDate
              )
            )
          }
        }
        if (replacedPatientPCR?.patient?.patientDemographics?.immunizationIncompleteReason) {
          if (!isFromPulsara) {
            yield put(
              setPatientImmunizationIncompleteReason(
                replacedPatientPCR?.patient?.patientDemographics?.immunizationIncompleteReason
              )
            )
          }
        }
        if (replacedPatientPCR?.patient?.patientImmunizations?.immunizations) {
          if (!isFromPulsara) {
            yield put(setImmunizations(replacedPatientPCR?.patient?.patientImmunizations?.immunizations))
          }
        }

        if (
          replacedPatientPCR?.patient?.patientAllergies?.allergies.length &&
          replacedPatientPCR?.patient?.patientAllergies?.allergies.filter(
            (allergy) => allergy.nemsisCode === noKnownDrugAllergiesNemsis
          ).length > 1
        ) {
          replacedPatientPCR.patient.patientAllergies.allergies =
            replacedPatientPCR.patient.patientAllergies.allergies.filter(
              (allergyObj) => allergyObj.nemsisCode !== noKnownDrugAllergiesNemsis
            )
          const alert: IAlertState = {
            header: i18n.t('patientAllergies.PatientAllergies'),
            message: i18n.t('patientAllergies.MultipleNKDAsAlert'),
            buttons: [{ text: 'Confirm' }],
          }
          yield put(showAlert(alert))
        }

        if (
          replacedPatientPCR?.patient?.patientDemographics?.phoneDetails &&
          phoneTypeOptions &&
          phoneTypeOptions.length > 0
        ) {
          const homePhoneType = phoneTypeOptions.find((option: IOption) => option.id === 1 || option.name === 'Home')

          if (homePhoneType) {
            replacedPatientPCR.patient.patientDemographics.phoneDetails.forEach((data) => {
              if (!data.type && data.number) {
                data.type = homePhoneType
              }
            })
          }
        }

        if (isFromPulsara) {
          yield call(initializePatientConflictWithProcessedData, replacedPatientPCR)
          return
        }

        yield call([pcrDocsService, pcrDocsService.savePCR], replacedPatientPCR)
        yield put(refreshPatientData())
        yield put(refreshBillingData())
      } else if (patientDataResponse.messages) {
        yield put(handleApiError(patientDataResponse))
      }
      yield put(setIsPatientDataLoading(false))
      yield put(resetSearch())
      yield put(resetSearchState())
    }
  } catch (e) {
    yield put(setIsPatientDataLoading(false))
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during patient search. Cause: ${e}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  } finally {
    yield put(resetLoader())
  }
}

export function* getPatientRecordSaga() {
  yield takeLatest([PatientSearchConstants.GET_PATIENT_RECORD], performWithMutexHOG, getPatientRecord)
}
